export const CF_ASSET_MIME = 'application/x-cf-asset'
export const URI_LIST_MIME = 'text/uri-list'

export const ASSET_MEDIA_TYPES = ['image', 'video', 'audio'] as const

export type AssetMediaType = (typeof ASSET_MEDIA_TYPES)[number]

export interface CfAssetDrag {
  mediaType: AssetMediaType
  url: string
  toolName: string
  id: string
  width?: number
  height?: number
  previewUrl?: string
}

export function serializeCfAssetDrag(asset: CfAssetDrag): string {
  return JSON.stringify(normalizeCfAssetDrag(asset))
}

export function parseCfAssetDrag(raw: string): CfAssetDrag | null {
  try {
    return validateCfAssetDrag(JSON.parse(raw))
  } catch {
    return null
  }
}

export function validateCfAssetDrag(value: unknown): CfAssetDrag | null {
  if (!isRecord(value)) {
    return null
  }
  const mediaType = assetMediaType(value.mediaType)
  const url = requiredText(value.url)
  const toolName = requiredText(value.toolName)
  const id = requiredText(value.id)
  if (!mediaType || !url || !toolName || !id) {
    return null
  }
  const width = optionalCount(value.width)
  const height = optionalCount(value.height)
  const previewUrl = optionalText(value.previewUrl)
  if (width === null || height === null || previewUrl === null) {
    return null
  }
  return {
    mediaType,
    url,
    toolName,
    id,
    ...(width ? { width } : {}),
    ...(height ? { height } : {}),
    ...(previewUrl ? { previewUrl } : {}),
  }
}

export function cfAssetDragAccepted(
  asset: CfAssetDrag,
  accept: readonly AssetMediaType[],
): boolean {
  return accept.includes(asset.mediaType)
}

export function readDroppedAsset(input: {
  types: readonly string[]
  assetRaw: string
}): CfAssetDrag | null {
  if (input.types.includes('Files') || !input.types.includes(CF_ASSET_MIME)) {
    return null
  }
  return parseCfAssetDrag(input.assetRaw)
}

export function acceptedDrop(input: {
  types: readonly string[]
  assetRaw: string
  accept: readonly AssetMediaType[]
}): CfAssetDrag | null {
  const asset = readDroppedAsset(input)
  if (!asset || !cfAssetDragAccepted(asset, input.accept)) {
    return null
  }
  return asset
}

/**
 * During dragover the payload is often unreadable. A same-document drag passes
 * the asset it started with; a foreign drag only exposes the MIME until drop.
 */
export function dragOverAccepted(input: {
  types: readonly string[]
  asset: CfAssetDrag | null
  accept: readonly AssetMediaType[]
}): boolean {
  if (input.types.includes('Files') || !input.types.includes(CF_ASSET_MIME)) {
    return false
  }
  if (!input.asset) {
    return true
  }
  return cfAssetDragAccepted(input.asset, input.accept)
}

function normalizeCfAssetDrag(asset: CfAssetDrag): CfAssetDrag {
  return {
    mediaType: asset.mediaType,
    url: asset.url,
    toolName: asset.toolName,
    id: asset.id,
    ...(asset.width ? { width: asset.width } : {}),
    ...(asset.height ? { height: asset.height } : {}),
    ...(asset.previewUrl ? { previewUrl: asset.previewUrl } : {}),
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function requiredText(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0) {
    return null
  }
  return value
}

function optionalText(value: unknown): string | null | undefined {
  if (value === undefined) {
    return undefined
  }
  return requiredText(value)
}

function optionalCount(value: unknown): number | null | undefined {
  if (value === undefined) {
    return undefined
  }
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null
  }
  return value
}

function assetMediaType(value: unknown): AssetMediaType | null {
  if (value === 'image' || value === 'video' || value === 'audio') {
    return value
  }
  return null
}
